import React, { useState, useMemo } from 'react';

const TAX_BRACKETS = [
  { label: '24%', value: 0.24 },
  { label: '32%', value: 0.32 },
  { label: '35%', value: 0.35 },
  { label: '37%', value: 0.37 },
];

const PA_CREDIT_RATE = 0.25;
const MIN_INVESTMENT = 10000;
const MAX_INVESTMENT = 500000;

function formatCurrency(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function BarChart({ investment, taxSavings, paCredit, netCost, isPA }) {
  const totalBenefit = taxSavings + paCredit;
  const maxBar = Math.max(investment, totalBenefit);

  const bars = [
    {
      label: 'TOTAL INVESTED',
      value: investment,
      color: 'bg-red-600',
      glow: 'shadow-[0_0_12px_rgba(220,38,38,0.4)]',
      tag: 'RISK',
    },
    {
      label: 'TAX DEDUCTION',
      value: taxSavings,
      color: 'bg-sky-500',
      glow: 'shadow-[0_0_12px_rgba(14,165,233,0.4)]',
      tag: 'BENEFIT',
    },
    ...(isPA
      ? [
          {
            label: 'PA 25% CREDIT',
            value: paCredit,
            color: 'bg-emerald-500',
            glow: 'shadow-[0_0_12px_rgba(16,185,129,0.4)]',
            tag: 'BENEFIT',
          },
        ]
      : []),
    {
      label: 'NET OUT-OF-POCKET',
      value: netCost,
      color: 'bg-zinc-600',
      glow: '',
      tag: 'NET',
    },
  ];

  return (
    <div className="space-y-4">
      {bars.map((bar) => {
        const pct = maxBar > 0 ? (bar.value / maxBar) * 100 : 0;
        return (
          <div key={bar.label}>
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-2">
                <span
                  className={`text-[10px] font-bold tracking-widest px-2 py-0.5 border ${
                    bar.tag === 'RISK'
                      ? 'border-red-600 text-red-500'
                      : bar.tag === 'BENEFIT'
                      ? 'border-sky-500 text-sky-400'
                      : 'border-zinc-600 text-zinc-400'
                  }`}
                >
                  {bar.tag}
                </span>
                <span className="text-[11px] text-zinc-400 tracking-widest uppercase">
                  {bar.label}
                </span>
              </div>
              <span className="text-sm font-bold text-white tabular-nums">
                {formatCurrency(bar.value)}
              </span>
            </div>
            <div className="h-3 w-full bg-zinc-900 rounded-sm overflow-hidden">
              <div
                className={`h-full rounded-sm transition-all duration-700 ease-out ${bar.color} ${bar.glow}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FilmTaxROI() {
  const [investment, setInvestment] = useState(50000);
  const [bracket, setBracket] = useState(0.37);
  const [isPA, setIsPA] = useState(false);

  const { taxSavings, paCredit, totalBenefit, netCost, roiPct } = useMemo(() => {
    const taxSavings = investment * bracket;
    const paCredit = isPA ? investment * PA_CREDIT_RATE : 0;
    const totalBenefit = taxSavings + paCredit;
    const netCost = Math.max(0, investment - totalBenefit);
    const roiPct = investment > 0 ? ((totalBenefit / investment) * 100).toFixed(1) : 0;
    return { taxSavings, paCredit, totalBenefit, netCost, roiPct };
  }, [investment, bracket, isPA]);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-[Inter,system-ui,sans-serif]">
      <div className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
        <a href="/#/tools" style={{ textDecoration: 'none' }} className="text-[11px] tracking-[0.2em] text-zinc-500 hover:text-sky-400 uppercase font-bold transition-colors">
          ← Aperture Slate Tools
        </a>
        <a href="/#/" style={{ textDecoration: 'none' }} className="text-[11px] tracking-[0.15em] text-zinc-700 hover:text-zinc-400 uppercase transition-colors">
          Main Site
        </a>
      </div>
      <div className="flex items-center justify-center p-6 pb-24">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[11px] tracking-[0.4em] text-sky-400 font-bold uppercase mb-3">
            Aperture Slate // Investor Tools
          </p>
          <h1 className="text-4xl font-black tracking-tight uppercase leading-none">
            Film Tax
            <br />
            <span className="text-zinc-500">ROI Calculator</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-4 leading-relaxed max-w-md">
            Estimate your after-tax cost of investing in a production slate using
            IRC §181 deductions and applicable state incentives.
          </p>
        </div>

        {/* Panel */}
        <div className="border border-zinc-800 bg-zinc-950 p-8 space-y-8">

          {/* Investment Slider */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">
                Investment Amount
              </label>
              <span className="text-2xl font-black tabular-nums text-white">
                {formatCurrency(investment)}
              </span>
            </div>
            <input
              type="range"
              min={MIN_INVESTMENT}
              max={MAX_INVESTMENT}
              step={5000}
              value={investment}
              onChange={(e) => setInvestment(Number(e.target.value))}
              className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
              <span>{formatCurrency(MIN_INVESTMENT)}</span>
              <span>{formatCurrency(MAX_INVESTMENT)}</span>
            </div>
          </div>

          {/* Tax Bracket */}
          <div>
            <label className="block text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-3">
              Federal Tax Bracket
            </label>
            <div className="grid grid-cols-4 gap-2">
              {TAX_BRACKETS.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setBracket(b.value)}
                  className={`py-3 text-sm font-bold tracking-widest border transition-all duration-200 ${
                    bracket === b.value
                      ? 'border-sky-500 text-sky-400 bg-sky-500/10 shadow-[0_0_16px_rgba(14,165,233,0.2)]'
                      : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* PA Toggle */}
          <div className="flex items-center justify-between border border-zinc-800 px-5 py-4">
            <div>
              <p className="text-sm font-bold tracking-wider text-white">
                Filming in Pennsylvania?
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5 tracking-wide">
                Adds the PA 25% Production Tax Credit
              </p>
            </div>
            <button
              onClick={() => setIsPA((v) => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none ${
                isPA ? 'bg-emerald-500' : 'bg-zinc-700'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${
                  isPA ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-800" />

          {/* Bar Chart */}
          <div>
            <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-5">
              Risk vs. Benefit
            </p>
            <BarChart
              investment={investment}
              taxSavings={taxSavings}
              paCredit={paCredit}
              netCost={netCost}
              isPA={isPA}
            />
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            <div className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
              <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">
                Total Benefit
              </p>
              <p className="text-lg font-black text-sky-400 tabular-nums">
                {formatCurrency(totalBenefit)}
              </p>
            </div>
            <div className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
              <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">
                Net Cost
              </p>
              <p className="text-lg font-black text-white tabular-nums">
                {formatCurrency(netCost)}
              </p>
            </div>
            <div className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
              <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">
                Benefit Rate
              </p>
              <p className="text-lg font-black text-emerald-400 tabular-nums">
                {roiPct}%
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
            Estimates only. IRC §181 allows immediate expensing of qualifying film
            production costs. PA credit subject to application and approval.
            Consult a qualified tax professional before making investment
            decisions.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
